/**
 * CollectorProfilePreview — Phase 4B claimed-collector card embedded on the
 * public certificate page.
 *
 * Data pipeline (public-safe only):
 *   1. `owner_public_collector_id` (GM-Cxxxxxx) is provided by the certificate
 *      registry payload.
 *   2. We resolve the collector's `username` via
 *      `GET /api/public/collectors/search?q=GM-Cxxxxxx`. This search endpoint
 *      already respects the collector's public/private setting — private
 *      profiles do not appear in results.
 *   3. If a username is found, we optionally fetch the full public profile
 *      (`GET /api/public/collectors/{username}`) to surface avatar, role/title
 *      (favorite_category) and a collection summary.
 *
 * Three rendering states:
 *   • unclaimed  — cert has no owner. Renders an "unclaimed" placeholder card.
 *   • private    — collector exists but keeps their profile private, or their
 *                  privacy flags hide everything meaningful. We render a
 *                  minimal claimed card without a public CTA.
 *   • public     — collector allows their profile publicly. Full preview with
 *                  avatar / name / role / collection summary + "View Collector
 *                  Profile" CTA.
 *
 * ZERO backend changes — every call hits public endpoints that already exist.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, ShieldCheck, Sparkles, ChevronRight, Lock } from "lucide-react";
import { API } from "@/lib/api";

/**
 * @typedef {Object} CollectorProfilePreviewProps
 * @property {boolean}          is_claimed
 * @property {string|null}      owner_public_collector_id  GM-Cxxxxxx
 * @property {string|null}      collector_name             from the cert record
 * @property {"dark"|"light"}   [theme]                    default "dark"
 */

export default function CollectorProfilePreview({
  is_claimed,
  owner_public_collector_id,
  collector_name,
  theme = "dark",
}) {
  const [state, setState] = useState({
    status: is_claimed && owner_public_collector_id ? "loading" : "idle",
    username: null,
    profile: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!is_claimed || !owner_public_collector_id) {
      setState({ status: "idle", username: null, profile: null });
      return;
    }
    setState({ status: "loading", username: null, profile: null });

    (async () => {
      try {
        // 1. Resolve username from the GM-C id. Public collectors only.
        const searchResp = await fetch(
          `${API}/public/collectors/search?q=${encodeURIComponent(owner_public_collector_id)}`
        );
        if (!searchResp.ok) throw new Error(`search-${searchResp.status}`);
        const search = await searchResp.json();
        const match = (search.results || [])
          .find((r) => r.public_collector_id === owner_public_collector_id)
          || (search.results && search.results[0]);
        if (!match || !match.username) {
          if (!cancelled) setState({ status: "private", username: null, profile: null });
          return;
        }

        // 2. Fetch full public profile for the resolved username.
        const profResp = await fetch(
          `${API}/public/collectors/${encodeURIComponent(match.username)}`
        );
        if (!profResp.ok) throw new Error(`profile-${profResp.status}`);
        const prof = await profResp.json();
        if (cancelled) return;

        if (!prof.profile || prof.private) {
          setState({ status: "private", username: match.username, profile: null });
        } else {
          setState({ status: "public", username: match.username, profile: prof.profile });
        }
      } catch {
        if (!cancelled) setState({ status: "private", username: null, profile: null });
      }
    })();

    return () => { cancelled = true; };
  }, [is_claimed, owner_public_collector_id]);

  const dark = theme === "dark";

  if (!is_claimed) {
    return <UnclaimedCard dark={dark} />;
  }

  if (state.status === "loading") {
    return <SkeletonCard dark={dark} />;
  }

  if (state.status === "public" && state.profile) {
    return (
      <PublicPreview
        dark={dark}
        username={state.username}
        profile={state.profile}
        publicId={owner_public_collector_id}
      />
    );
  }

  // Private / unresolved — still a claimed cert, just no public disclosure.
  return (
    <PrivateClaimedCard
      dark={dark}
      publicId={owner_public_collector_id}
      collectorName={collector_name}
    />
  );
}

// ─────────────────────────────────────────────────────────────
function SectionShell({ dark, testid, children }) {
  return (
    <section
      data-testid={testid || "collector-profile-preview"}
      className={
        "rounded-3xl p-6 md:p-8 " +
        (dark
          ? "border border-white/10 bg-white/[0.03]"
          : "border-2 border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--ink)]")
      }
    >
      {children}
    </section>
  );
}

function EyebrowLabel({ dark, children }) {
  return (
    <div className={"text-[10px] uppercase tracking-[0.35em] font-bold " +
      (dark ? "text-[var(--collector-gold-soft)]" : "text-[var(--panini-red)]")}>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function PublicPreview({ dark, username, profile, publicId }) {
  const avatarUrl = profile.avatar_media_id
    ? `${API}/public/collectors/${encodeURIComponent(username)}/avatar?v=${encodeURIComponent(profile.avatar_updated_at || "")}`
    : null;

  const roleTitle = profile.favorite_category
    ? String(profile.favorite_category).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : (profile.bio ? "Verified Collector" : "Verified Collector");

  const stats = profile.album_stats || {};
  const summary = [
    stats.albums_completed ? `${stats.albums_completed} completed` : null,
    stats.albums_currently_owned ? `${stats.albums_currently_owned} in collection` : null,
    stats.albums_total_ever_registered ? `${stats.albums_total_ever_registered} total registered` : null,
  ].filter(Boolean);

  return (
    <SectionShell dark={dark} testid="collector-profile-preview-public">
      <EyebrowLabel dark={dark}>Claimed Collector</EyebrowLabel>
      <div className="mt-4 flex items-start gap-5">
        {/* Avatar */}
        <div className={"w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shrink-0 grid place-items-center " +
          (dark ? "bg-white/5 border border-[var(--collector-gold)]/40" : "bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)]")}
          data-testid="collector-profile-preview-avatar">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={profile.display_name || "Collector avatar"}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <User size={38} className={dark ? "text-white/60" : "text-[var(--ink)]"} aria-hidden />
          )}
        </div>

        {/* Identity */}
        <div className="min-w-0 flex-1">
          <div
            data-testid="collector-profile-preview-name"
            className={"font-display font-bold leading-tight text-2xl md:text-3xl " + (dark ? "text-white" : "text-[var(--ink)]")}
          >
            {profile.display_name || "Verified Collector"}
          </div>
          <div className={"mt-1 flex flex-wrap items-center gap-2 text-xs " + (dark ? "text-white/60" : "text-[var(--smoke)]")}>
            {profile.username && (
              <span data-testid="collector-profile-preview-username" className="font-mono">@{profile.username}</span>
            )}
            {publicId && (
              <span
                data-testid="collector-profile-preview-public-id"
                className={"font-mono font-bold px-2 py-0.5 rounded-full " +
                  (dark ? "bg-[var(--collector-gold)]/15 text-[var(--collector-gold-soft)]" : "bg-[var(--panini-yellow-soft)] text-[var(--ink)]")}
              >
                {publicId}
              </span>
            )}
          </div>

          {/* Role/Title */}
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest"
               data-testid="collector-profile-preview-role">
            <Sparkles size={12} className={dark ? "text-[var(--collector-gold)]" : "text-[var(--panini-red)]"} aria-hidden />
            <span className={dark ? "text-white/80" : "text-[var(--ink)]"}>{roleTitle}</span>
          </div>

          {/* Collection Summary */}
          {summary.length > 0 && (
            <div
              className={"mt-4 text-sm leading-relaxed " + (dark ? "text-white/70" : "text-[var(--ink)]")}
              data-testid="collector-profile-preview-summary"
            >
              {summary.join(" · ")}
            </div>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-6">
        <Link
          to={`/collectors/${username}`}
          data-testid="collector-profile-preview-cta"
          className={
            "inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm md:text-base font-display uppercase tracking-wide transition " +
            (dark
              ? "bg-white text-[var(--ink)] border-2 border-[var(--ink)] hover:bg-[var(--collector-gold-soft)]"
              : "bg-[var(--panini-red)] text-white border-2 border-[var(--ink)] hover:brightness-110")
          }
        >
          <User size={15} /> View Collector Profile <ChevronRight size={15} />
        </Link>
      </div>
    </SectionShell>
  );
}

// ─────────────────────────────────────────────────────────────
function PrivateClaimedCard({ dark, publicId, collectorName }) {
  return (
    <SectionShell dark={dark} testid="collector-profile-preview-private">
      <EyebrowLabel dark={dark}>Claimed Collector</EyebrowLabel>
      <div className="mt-4 flex items-start gap-4">
        <div className={"w-16 h-16 rounded-full grid place-items-center shrink-0 " +
          (dark ? "bg-white/5 border border-white/15" : "bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)]")}>
          <Lock size={22} className={dark ? "text-white/60" : "text-[var(--ink)]"} aria-hidden />
        </div>
        <div>
          <div className={"font-display text-xl md:text-2xl " + (dark ? "text-white" : "text-[var(--ink)]")}
               data-testid="collector-profile-preview-private-name">
            {collectorName || "A verified collector"}
          </div>
          <div className={"mt-1 inline-flex items-center gap-2 text-xs " + (dark ? "text-white/70" : "text-[var(--smoke)]")}>
            <ShieldCheck size={12} className={dark ? "text-[var(--collector-gold)]" : "text-emerald-900"} />
            Ownership recorded on CollectorUp
            {publicId && (
              <span className={"font-mono font-bold px-2 py-0.5 rounded-full " +
                (dark ? "bg-[var(--collector-gold)]/15 text-[var(--collector-gold-soft)]" : "bg-[var(--panini-yellow-soft)] text-[var(--ink)]")}>
                {publicId}
              </span>
            )}
          </div>
          <p className={"mt-3 text-sm max-w-md " + (dark ? "text-white/55" : "text-[var(--smoke)]")}>
            This collector chose to keep their public profile private. The
            certificate itself remains verified and permanently recorded.
          </p>
        </div>
      </div>
    </SectionShell>
  );
}

// ─────────────────────────────────────────────────────────────
function UnclaimedCard({ dark }) {
  return (
    <SectionShell dark={dark} testid="collector-profile-preview-unclaimed">
      <EyebrowLabel dark={dark}>Awaiting Claim</EyebrowLabel>
      <div className="mt-4 flex items-start gap-4">
        <div className={"w-16 h-16 rounded-full grid place-items-center shrink-0 " +
          (dark ? "bg-white/5 border border-white/15" : "bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)]")}>
          <User size={24} className={dark ? "text-white/60" : "text-[var(--ink)]"} aria-hidden />
        </div>
        <div>
          <div className={"font-display text-xl md:text-2xl " + (dark ? "text-white" : "text-[var(--ink)]")}
               data-testid="collector-profile-preview-unclaimed-title">
            Collector profile not yet linked
          </div>
          <p className={"mt-3 text-sm max-w-md " + (dark ? "text-white/55" : "text-[var(--smoke)]")}>
            Once the owner claims this certificate, their verified CollectorUp
            profile will appear here.
          </p>
        </div>
      </div>
    </SectionShell>
  );
}

// ─────────────────────────────────────────────────────────────
function SkeletonCard({ dark }) {
  return (
    <SectionShell dark={dark} testid="collector-profile-preview-loading">
      <div className="animate-pulse flex items-start gap-5">
        <div className={"w-20 h-20 md:w-24 md:h-24 rounded-full " + (dark ? "bg-white/10" : "bg-[var(--stone)]")} />
        <div className="flex-1 space-y-3">
          <div className={"h-7 w-2/3 rounded " + (dark ? "bg-white/10" : "bg-[var(--stone)]")} />
          <div className={"h-4 w-1/3 rounded " + (dark ? "bg-white/10" : "bg-[var(--stone)]")} />
          <div className={"h-4 w-1/2 rounded " + (dark ? "bg-white/10" : "bg-[var(--stone)]")} />
        </div>
      </div>
    </SectionShell>
  );
}
